import * as Sentry from "@sentry/react";
import type { ErrorEvent, EventHint } from "@sentry/react";
import { getAppVersion } from "../version";

export interface SentryUser {
  id?: string;
  username?: string;
  email?: string;
}

const isSentryEnabled = () => import.meta.env.VITE_APP_PHASE === "production";

export const initSentry = (dsn: string | undefined) => {
  if (!isSentryEnabled()) {
    return;
  }

  if (!dsn) {
    console.warn("Sentry DSN not provided. Error tracking disabled.");
    return;
  }

  const version = getAppVersion();

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_APP_PHASE,
    release: version.buildVersion,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.captureConsoleIntegration({
        levels: ["error"],
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event: ErrorEvent, hint: EventHint) {
      if (event.exception) {
        const error = hint.originalException;

        if (error instanceof Error) {
          const errorMessage = error.message.toLowerCase();

          if (
            errorMessage.includes("resizeobserver loop limit exceeded") ||
            errorMessage.includes("non-error promise rejection captured") ||
            errorMessage.includes("chunk load") ||
            errorMessage.includes("loading chunk")
          ) {
            return null;
          }
        }
      }

      if (event.request?.url) {
        const url = event.request.url.toLowerCase();
        if (
          url.includes("chrome-extension://") ||
          url.includes("moz-extension://") ||
          url.includes("safari-extension://")
        ) {
          return null;
        }
      }

      return event;
    },
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
      "ChunkLoadError",
      "Loading chunk",
      "NetworkError",
      "Network request failed",
    ],
    denyUrls: [
      /chrome-extension:\/\//i,
      /moz-extension:\/\//i,
      /safari-extension:\/\//i,
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
    ],
  });

  Sentry.setTag("app_version", version.version);
  Sentry.setTag("build_version", version.buildVersion);
  Sentry.setTag("build_timestamp", version.buildTimestamp);
};

export const setSentryUser = (user: SentryUser | null) => {
  if (!isSentryEnabled()) {
    return;
  }

  if (user) {
    Sentry.setUser({
      id: user.id,
      username: user.username,
      email: user.email,
    });
  } else {
    Sentry.setUser(null);
  }
};

export const captureException = (
  error: Error | unknown,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    level?: Sentry.SeverityLevel;
  },
) => {
  if (!isSentryEnabled()) {
    return;
  }

  if (context?.tags) {
    Object.entries(context.tags).forEach(([key, value]) => {
      Sentry.setTag(key, value);
    });
  }

  if (context?.extra) {
    Sentry.setExtra("context", context.extra);
  }

  Sentry.captureException(error, {
    level: context?.level || "error",
  });
};

export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = "info",
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  },
) => {
  if (!isSentryEnabled()) {
    return;
  }

  if (context?.tags) {
    Object.entries(context.tags).forEach(([key, value]) => {
      Sentry.setTag(key, value);
    });
  }

  if (context?.extra) {
    Sentry.setExtra("context", context.extra);
  }

  Sentry.captureMessage(message, level);
};

export const addBreadcrumb = (
  message: string,
  category?: string,
  level: Sentry.SeverityLevel = "info",
  data?: Record<string, unknown>,
) => {
  if (!isSentryEnabled()) {
    return;
  }

  Sentry.addBreadcrumb({
    message,
    category: category || "default",
    level,
    data,
    timestamp: Date.now() / 1000,
  });
};

export const setSentryContext = (key: string, context: Record<string, unknown>) => {
  if (!isSentryEnabled()) {
    return;
  }

  Sentry.setContext(key, context);
};

export const startSentryTransaction = <T>(name: string, op: string, callback: () => T): T => {
  if (!isSentryEnabled()) {
    return callback();
  }

  return Sentry.startSpan(
    {
      name,
      op,
    },
    callback,
  );
};
